import { createTheme, ThemeOptions } from "@mui/material/styles";
import colors from "./styling/colors";
import { Opacity } from "@mui/icons-material";
declare module "@mui/material/styles" {
  interface Palette {
    purple: PaletteColor;
  }
  interface PaletteColor {
    hover?: string;
    selected?: string;
  }
  interface TypographyVariantsOptions {
    grey: React.CSSProperties;
  }
  interface TypographyVariants {
    grey: React.CSSProperties;
  }
}

declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    grey: true;
  }
}

declare module "@mui/material/LinearProgress" {
  interface LinearProgressPropsColorOverrides {
    purple: true;
  }
}

declare module "@mui/material/Button" {
  interface ButtonPropsColorOverrides {
    purple: true;
  }
}

const theme = createTheme({
  components: {
    typography: {
      fontFamily: [
        '"Poppins"',
        "Roboto",
        "Helvetica Neue",
        "Arial",
        "sans-serif",
      ].join(","),
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: "#266A4A",
          "&.Mui-checked": {
            color: "#266A4A",
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 24,
          textTransform: "none",
          letterSpacing: "0.1px",
          boxShadow: "none",
        },
      },
      variants: [
        {
          props: { variant: "text" },
          style: {
            // borderRadius: 16,
            color: "#2E7D32",
            textDecoration: "none",
            "&:hover": {
              backgroundColor: "transparent",
            },
          },
        },
        {
          props: { variant: "text", color: "purple" },
          style: {
            // borderRadius: 16,
            color: "#7C4DFF",
            textDecoration: "none",
            "&:hover": {
              backgroundColor: "transparent",
            },
          },
        },
        {
          props: { variant: "grey" },
          style: {
            // borderRadius: 16,
            color: "rgba(0, 0, 0, 0.40)",
            textDecoration: "none",
            "&:hover": {
              backgroundColor: "transparent",
            },
          },
        },
        {
          props: { variant: "contained" },
          style: {
            // borderRadius: 16,
            backgroundColor: "#1976D2",
            color: colors.white,
          },
        },
        {
          props: { variant: "secondary" },
          style: {
            // borderRadius: 16,
            backgroundColor: colors.black["900"],
            color: colors.white,
          },
        },
        {
          props: { variant: "filled" },
          style: {
            "&:hover": {
              backgroundColor: "#2E7D32",
              color: colors.white,
            },
          },
        },
        {
          props: { variant: "green" },
          style: {
            // borderRadius: 24,
            backgroundColor: "#266A4A",
            color: colors.white,
            "&:hover": {
              backgroundColor: "#2E7D32",
            },
          },
        },
        {
          props: { variant: "topnav" },
          style: {
            "&.selected": {
              background: "rgba(38, 106, 74, 0.12)",
            },
            // borderRadius: 16,
            backgroundColor: "transparent",
            border: "none",
            color: "#266A4A",
            fontSize: 14,
            "&:hover": {
              background: "rgba(38, 106, 74, 0.12)",
            },
          },
        },
        {
          props: { variant: "purple" },
          style: {
            boxShadow:
              "0px 1px 5px 0px rgba(0, 0, 0, 0.12), 0px 2px 2px 0px rgba(0, 0, 0, 0.14), 0px 3px 1px -2px rgba(0, 0, 0, 0.20)",
            borderRadius: "40px",
            backgroundColor: "#7C4DFF",
            border: `1px solid rgba(124, 77, 255, 0.50)`,
            color: "white",
            fontSize: 14,
            // padding: "8px 22px",
            "&:hover": {
              background: "#7C4DFFAA",
            },
          },
        },
      ],
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: "8px",
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          color: "#000",
          fontSize: 12,
          fontStyle: "normal",
          letterSpacing: "0.4px",
          padding: 8,
          backgroundColor: "#F5F5F5",
          border: "1px solid #ccc",
          borderRadius: 5,
          maxWidth: 400,
          minWidth: 400,
        },
      },
    },
    MuiChip: {
      defaultProps: {
        size: "small",
      },
      styleOverrides: {
        root: {
          "& .MuiChip-label": {
            fontSize: "0.75rem",
          },
          border: "none",
          backgroundColor: "#D0E8D7",
          color: "#1B5E20",
          borderRadius: 2,
          paddingRight: 2,
          paddingLeft: 2,
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          color: colors.purple.dark,
          "&.Mui-checked": {
            color: colors.purple.dark,
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: "3px 3px 15px 0px rgba(203, 185, 231, 0.23)",
          padding: 16,
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {},
      },
    },
    MuiCardActions: {
      styleOverrides: {
        root: {
          width: "100%",
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: ({ theme }) => ({
          ...theme.typography.body2, // or whichever variant you want to use
          marginTop: 4,
        }),
      },
    },
  },
  palette: {
    text: {
      primary: "#000000",
      secondary: "#666666",
      disabled: "#B3B3B3",
    },
    primary: {
      main: "#1976D2",
      dark: "#2E7D32",
      light: "#43A047",
      contrastText: "#ffffff",
      hover: "rgba(25, 118, 210, 0.04)",
      selected: "rgba(25, 118, 210, 0.08)",
    },
    secondary: {
      main: "#9C27B0",
      dark: "#7B1FA2",
      light: "#BA68C8",
      contrastText: "#ffffff",
      hover: "rgba(156, 39, 176, 0.04)",
      selected: "rgba(156, 39, 176, 0.08)",
    },
    error: {
      main: "#D32F2F",
      dark: "#C62828",
      light: "#EF5350",
      contrastText: "#ffffff",
    },
    warning: {
      main: "#EF6C00",
      dark: "#E65100",
      light: "#FF9800",
      contrastText: "#ffffff",
    },
    info: {
      main: "#0288D1",
      dark: "#01579B",
      light: "#03A9F4",
      contrastText: "#ffffff",
    },
    success: {
      main: "#2E7D32",
      dark: "#1B5E20",
      light: "#4CAF50",
      contrastText: "#ffffff",
    },
    background: {
      default: "#fafafa",
      paper: "#ffffff",
      elevation0: "rgba(255, 255, 255, 0.7)",
      elevation2: "rgba(255, 255, 255, 0.85)",
      elevation16: "rgba(255, 255, 255, 0.9)",
      elevation24: "rgba(255, 255, 255, 0.95)",
    },
    action: {
      active: "#000000",
      hover: "rgba(0, 0, 0, 0.04)",
      selected: "rgba(0, 0, 0, 0.08)",
      disabledBackground: "rgba(0, 0, 0, 0.12)",
      focus: "rgba(0, 0, 0, 0.12)",
      disabled: "#B3B3B3",
    },
    common: {
      white: "#ffffff",
      black: "#000000",
    },
    purple: {
      main: "#7C4DFF",
      dark: "#5E35B1",
      light: "#B388FF",
      contrastText: "#ffffff",
      hover: "rgba(124, 77, 255, 0.04)",
      selected: "rgba(124, 77, 255, 0.08)",
    },
  },
  typography: {
    fontFamily: "Poppins, Arial, sans-serif",
    h1: {
      fontSize: "6rem",
      lineHeight: 1.17,
      fontWeight: 300,
      letterSpacing: "-0.01562em",
    },
    h2: {
      fontSize: "3.75rem",
      lineHeight: 1.2,
      fontWeight: 300,
      letterSpacing: "-0.00833em",
    },
    h3: {
      fontSize: "3rem",
      lineHeight: 1.17,
      fontWeight: 400,
      letterSpacing: "0em",
    },
    h4: {
      fontSize: "2.125rem",
      lineHeight: 1.41,
      fontWeight: 400,
      letterSpacing: "0.00735em",
    },
    h5: {
      fontSize: "1.5rem!important",
      lineHeight: 1.33,
      fontWeight: 400,
      letterSpacing: "0em",
    },
    h6: {
      fontSize: "1.25rem",
      lineHeight: 1.6,
      fontWeight: 500,
      letterSpacing: "0.0075em",
    },
    subtitle1: {
      fontSize: "1rem",
      lineHeight: 1.75,
      fontWeight: 400,
      letterSpacing: "0.00938em",
    },
    subtitle2: {
      fontSize: "0.875rem",
      lineHeight: 1.57,
      fontWeight: 500,
      letterSpacing: "0.00714em",
    },
    body1: {
      fontSize: "1rem",
      lineHeight: 1.5,
      fontWeight: 400,
      letterSpacing: "0.03125em",
    },
    body2: {
      fontSize: "0.875rem",
      lineHeight: "112%",
      fontWeight: 400,
      letterSpacing: "0.4px",
    },
    button: {
      fontSize: "0.875rem",
      lineHeight: 1.75,
      fontWeight: 500,
      letterSpacing: "0.08929em",
      textTransform: "uppercase",
    },
    caption: {
      fontSize: "0.75rem!important",
      lineHeight: 1.66,
      fontWeight: 400,
      letterSpacing: "0.03333em",
    },
    overline: {
      fontSize: "0.75rem",
      lineHeight: 2.66,
      fontWeight: 400,
      letterSpacing: "0.16667em",
      textTransform: "uppercase",
    },
    grey: {
      fontSize: "21px", // Converted from 1rem
      fontWeight: 400,
      lineHeight: 1.5,
      textDecoration: "none",
      color: "rgba(0, 0, 0, 0.40)",
    },
  },
} as ThemeOptions);

export default theme;
